/**
 * Site-wide navigation bar.
 * Shows different links depending on auth state.
 */

import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Coins,
  LayoutDashboard,
  ShoppingCart,
  History,
  Settings,
  LogOut,
  Menu,
  X,
  LogIn,
} from 'lucide-react';

export default function Navbar() {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isConnectPage = location.pathname === '/connect';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path: string) => location.pathname === path;

  const navLink = (to: string, label: string, Icon: React.FC<{ size?: number }>) => (
    <Link
      to={to}
      className={`nav-link ${isActive(to) ? 'active' : ''}`}
      onClick={() => setMobileOpen(false)}
    >
      <Icon size={18} />
      <span>{label}</span>
    </Link>
  );

  return (
    <nav className="navbar">
      <div className="navbar-inner">
        <Link to="/" className="navbar-brand" onClick={() => setMobileOpen(false)}>
          <Coins size={28} />
          <span>AI Tokens</span>
        </Link>

        {/* Desktop nav */}
        <div className="navbar-links desktop-only">
          {isConnectPage ? (
            <>
              <button className="nav-link logout-btn" onClick={handleLogout}>
                <LogOut size={18} />
                <span>Logout</span>
              </button>
            </>
          ) : isAuthenticated ? (
            <>
              {navLink('/dashboard', 'Dashboard', LayoutDashboard)}
              {navLink('/purchase', 'Get Tokens', ShoppingCart)}
              {navLink('/transactions', 'History', History)}
              {navLink('/settings', 'Settings', Settings)}
              <button className="nav-link logout-btn" onClick={handleLogout}>
                <LogOut size={18} />
                <span>Logout</span>
              </button>
              <div className="nav-user-badge">
                <span>{user?.name}</span>
              </div>
            </>
          ) : (
            <>
              <a href="/#how-it-works" className="nav-link">How It Works</a>
              <a href="/#pricing" className="nav-link">Pricing</a>
              <Link to="/login" className="nav-link">
                <LogIn size={18} />
                <span>Sign In</span>
              </Link>
              <Link to="/register" className="btn btn-primary btn-sm">
                Get Started
              </Link>
            </>
          )}
        </div>

        {/* Mobile toggle */}
        <button
          className="mobile-menu-btn mobile-only"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle navigation"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="navbar-mobile">
          {isConnectPage ? (
            <>
              <button className="nav-link logout-btn" onClick={handleLogout}>
                <LogOut size={18} />
                <span>Logout</span>
              </button>
            </>
          ) : isAuthenticated ? (
            <>
              {navLink('/dashboard', 'Dashboard', LayoutDashboard)}
              {navLink('/purchase', 'Get Tokens', ShoppingCart)}
              {navLink('/transactions', 'History', History)}
              {navLink('/settings', 'Settings', Settings)}
              <button className="nav-link logout-btn" onClick={handleLogout}>
                <LogOut size={18} />
                <span>Logout</span>
              </button>
            </>
          ) : (
            <>
              <a
                href="/#how-it-works"
                className="nav-link"
                onClick={() => setMobileOpen(false)}
              >
                How It Works
              </a>
              <a
                href="/#pricing"
                className="nav-link"
                onClick={() => setMobileOpen(false)}
              >
                Pricing
              </a>
              <Link to="/login" className="nav-link" onClick={() => setMobileOpen(false)}>
                Sign In
              </Link>
              <Link
                to="/register"
                className="btn btn-primary"
                onClick={() => setMobileOpen(false)}
              >
                Get Started
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
